/**
 * Toast notification system
 * Singleton class for displaying toast notifications
 */
class ToastManager {
    constructor() {
        this.containers = {};
        this.toasts = [];
        this.positions = [
            'top-left', 'top-center', 'top-right',
            'bottom-left', 'bottom-center', 'bottom-right'
        ];
        this.createContainers();
    }

    /**
     * Create container elements for each position
     */
    createContainers() {
        // Create main container
        const mainContainer = document.createElement('div');
        mainContainer.className = 'toast-container';
        document.body.appendChild(mainContainer);

        // Create position-specific containers
        this.positions.forEach(position => {
            const container = document.createElement('div');
            container.className = `toast-container-${position}`;
            mainContainer.appendChild(container);
            this.containers[position] = container;
        });
    }

    /**
     * Create and display a toast notification
     * @param {Object} options - Toast configuration options
     * @returns {Object} - Toast instance with close method
     */
    fire(options) {
        const toast = this.createToast(options);
        this.toasts.push(toast);

        return {
            close: () => this.removeToast(toast.id)
        };
    }

    /**
     * Create a toast element
     * @param {Object} options - Toast configuration
     * @returns {Object} - Toast element and metadata
     */
    createToast({
        title = '',
        message = '',
        duration = 3000,
        from = 'top-right',
        buttonText = '',
        onClick = null,
        type = 'success'
    }) {
        // Validate position
        if (!this.positions.includes(from)) {
            from = 'top-right'; // Default position
        }

        // Create toast ID
        const id = `toast-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

        // Create toast element
        const toast = document.createElement('div');
        toast.className = `toast toast-${from} toast-${type}`;
        toast.id = id;
        toast.setAttribute('role', 'alert');
        toast.setAttribute('aria-live', 'assertive');

        // Create toast content
        let toastHTML = '';

        // Add title if provided
        if (title) {
            toastHTML += `<div class="toast-title">${title}</div>`;
        }

        // Add message if provided
        if (message) {
            toastHTML += `<div class="toast-message">${message}</div>`;
        }

        // Add button if text is provided
        if (buttonText) {
            toastHTML += `<button class="toast-button">${buttonText}</button>`;
        }

        // Add progress bar
        toastHTML += `<div class="toast-progress"><div class="toast-progress-bar"></div></div>`;

        // Set toast content
        toast.innerHTML = toastHTML;

        // Add close button
        const closeButton = document.createElement('button');
        closeButton.className = 'toast-close';
        closeButton.innerHTML = '&times;';
        closeButton.setAttribute('aria-label', 'Close');
        toast.appendChild(closeButton);

        // Add to appropriate container based on position
        const container = this.containers[from];

        // For top positions, add to end (newest at bottom)
        // For bottom positions, add to beginning (newest at top)
        if (from.startsWith('top')) {
            container.appendChild(toast);
        } else {
            container.insertBefore(toast, container.firstChild);
        }

        // Add event listeners
        closeButton.addEventListener('click', () => this.removeToast(id));

        if (buttonText && onClick) {
            const actionButton = toast.querySelector('.toast-button');
            actionButton.addEventListener('click', () => onClick(() => this.removeToast(id)));
        }

        // Start progress bar animation
        const progressBar = toast.querySelector('.toast-progress-bar');
        if (duration > 0) {
            progressBar.style.transition = `width ${duration}ms linear`;

            // Force a reflow to ensure transition works
            void toast.offsetWidth;

            progressBar.style.width = '0%';

            // Set timeout to remove toast when duration expires
            setTimeout(() => this.removeToast(id), duration);
        } else {
            progressBar.style.display = 'none';
        }

        // Force a reflow then add the visible class to trigger entrance animation
        void toast.offsetWidth;
        toast.classList.add('visible');

        return {
            id,
            element: toast,
            direction: from
        };
    }

    /**
     * Remove a toast from the container
     * @param {string} id - Toast ID
     */
    removeToast(id) {
        const index = this.toasts.findIndex(toast => toast.id === id);
        if (index === -1) return;

        const toast = this.toasts[index];
        const element = toast.element;

        // Remove from toasts array
        this.toasts.splice(index, 1);

        // Store the original height for animation
        const originalHeight = element.offsetHeight;

        // Add the removing class for height animation
        element.classList.add('removing');
        element.classList.remove('visible');

        // Remove from DOM after animation completes
        let removed = false;

        // Listen for transition end to remove element
        const handleTransitionEnd = (e) => {
            // Only proceed if the element hasn't been removed yet
            if (removed) return;

            // Check if this is one of the transition properties we care about
            if (e.propertyName === 'height' || e.propertyName === 'opacity' || e.propertyName === 'transform') {
                element.remove();
                removed = true;
            }
        };

        element.addEventListener('transitionend', handleTransitionEnd);

        // Safety timeout to ensure removal if transition events don't fire
        setTimeout(() => {
            if (!removed) {
                element.remove();
            }
        }, 1000); // 1 second safety timeout
    }
}

/**
 * Toast singleton instance
 */
const Toast = (function () {
    let instance;

    function createInstance() {
        return new ToastManager();
    }

    return {
        /**
         * Display a toast notification
         * @param {Object} options - Toast configuration
         * @returns {Object} - Toast instance with close method
         */
        fire: function (options) {
            if (!instance) {
                instance = createInstance();
            }
            return instance.fire(options);
        }
    };
})();

// Make Toast available globally
window.Toast = Toast;
